import React, { useState, useEffect } from "react";
import {
  IoClose,
  IoChevronBack,
  IoChevronForward,
  IoPlayCircle,
} from "react-icons/io5";
import config from "../../config";

const ImageGalleryModal = ({
  images = [],
  initialIndex = 0,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleNext = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setIsZoomed(false);
  };

  const handlePrev = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setIsZoomed(false);
  };

  const currentImage = images[currentIndex];
  // Helper to check if string is video url (basic check) or if object has type 'video'
  const isVideo = (url) => {
    if (typeof url !== "string") return false;
    // Check for video file extensions or if the path contains video-related keywords
    return (
      url.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i) ||
      url.toLowerCase().includes("/video/") ||
      url.toLowerCase().includes("video")
    );
  };

  const S3_BASE_URL = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL;
  const getImageUrl = (img) => {
    if (!img) return "";
    return img.includes("http") ? img : `${S3_BASE_URL}/${img}`;
  };

  const renderContent = () => {
    const url = getImageUrl(currentImage);

    if (isVideo(url)) {
      return (
        <video
          src={url}
          className="max-h-[85vh] max-w-full object-contain"
          controls
          autoPlay
        />
      );
    }
    return (
      <img
        src={url}
        alt={`Gallery ${currentIndex}`}
        className={`max-h-[85vh] max-w-full object-contain transition-transform duration-300 ${
          isZoomed ? "scale-150 cursor-zoom-out" : "cursor-zoom-in"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          setIsZoomed(!isZoomed);
        }}
      />
    );
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#000000c9] bg-opacity-95 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Close Button */}
      <button
        className="absolute top-4 right-4 text-white text-4xl hover:text-orange-500 z-[110]"
        onClick={onClose}
      >
        <IoClose />
      </button>

      {/* Navigation - Left */}
      <button
        className="absolute left-2 sm:left-4 text-white text-4xl sm:text-6xl hover:text-orange-500 z-[110] p-2"
        onClick={handlePrev}
      >
        <IoChevronBack />
      </button>

      {/* Main Content Area */}
      <div className="relative w-full h-full flex flex-col items-center justify-center p-4">
        {renderContent()}

        {/* Counter/Caption */}
        <div className="absolute bottom-4 left-0 right-0 text-center text-white font-medium text-lg">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Navigation - Right */}
      <button
        className="absolute right-2 sm:right-4 text-white text-4xl sm:text-6xl hover:text-orange-500 z-[110] p-2"
        onClick={handleNext}
      >
        <IoChevronForward />
      </button>

      {/* Thumbnails strip (Optional, for "Show All Photos" feel) */}
      <div
        className="absolute bottom-12 left-0 right-0 flex justify-center gap-2 overflow-x-auto px-4 py-2"
        onClick={(e) => e.stopPropagation()}
      >
        {images.map((img, idx) => (
          <div
            key={idx}
            className={`w-12 h-12 sm:w-16 sm:h-16 flex-shrink-0 cursor-pointer border-2 rounded-md overflow-hidden ${
              currentIndex === idx
                ? "border-orange-500"
                : "border-transparent opacity-60 hover:opacity-100"
            }`}
            onClick={() => {
              setCurrentIndex(idx);
              setIsZoomed(false);
            }}
          >
            {isVideo(getImageUrl(img)) ? (
              <div className="w-full h-full bg-gray-800 flex items-center justify-center text-white">
                <IoPlayCircle className="text-2xl" />
              </div>
            ) : (
              <img
                src={getImageUrl(img)}
                alt=""
                className="w-full h-full object-cover"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImageGalleryModal;
